import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, uploadImage } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getAllArticles, addArticle, updateArticle, deleteArticle } from '../services/articleService';
import { seedArticles } from '../utils/seedArticles';
import './Admin.css';
import Footer from '../unAuth/Components/footer';

const Admin = () => {
  const [articles, setArticles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);

    // Check for Firebase authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCheckingAuth(false);
      if (user) {
        // Check if the user is an admin (case-insensitive, trimmed)
        const adminEmails = ['nhall@blueviewgroup.com', 'steve@perryelec.com'];
        const userEmail = user.email ? user.email.toLowerCase().trim() : '';
        const isAdmin = adminEmails.some(email => email.toLowerCase().trim() === userEmail);
        
        console.log('Auth check:', { 
          userEmail, 
          adminEmails, 
          isAdmin,
          rawEmail: user.email 
        });
        
        if (isAdmin) {
          console.log('User authenticated as admin');
          setIsAuthenticated(true);
        } else {
          console.warn('User not authorized:', userEmail, 'Expected one of:', adminEmails);
          setIsAuthenticated(false);
          // Don't auto-signout, let them see the error
          setLoginError(`Access denied. Email ${userEmail} is not authorized.`);
          signOut(auth);
        }
      } else {
        setIsAuthenticated(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Load articles from Firestore
  useEffect(() => {
    const loadArticles = async () => {
      const result = await getAllArticles();
      if (result.success) {
        setArticles(result.data);
      } else {
        console.error('Error loading articles:', result.error);
        setArticles([]);
      }
    };
    
    if (isAuthenticated) {
      loadArticles();
    }
  }, [isAuthenticated]);

  // No need to save to localStorage anymore - using Firestore

  const handleAddArticle = () => {
    if (!isAuthenticated) {
      alert('You must be logged in to add articles.');
      return;
    }
    setIsEditing(false);
    setEditingArticle(null);
    setShowForm(true);
  };

  const handleEditArticle = (article) => {
    if (!isAuthenticated) {
      alert('You must be logged in to edit articles.');
      return;
    }
    setIsEditing(true);
    setEditingArticle(article);
    setShowForm(true);
  };

  const handleDeleteArticle = async (id) => {
    // Check if user is authenticated as admin
    if (!isAuthenticated) {
      alert('You must be logged in as an admin to delete articles.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this article?')) {
      const result = await deleteArticle(id);
      if (result.success) {
        // Update local state
        setArticles(articles.filter(article => article.id !== id));
        alert('Article deleted successfully!');
      } else {
        alert('Error deleting article: ' + result.error);
      }
    }
  };

  const handleSaveArticle = async (articleData) => {
    // Check if user is authenticated as admin
    if (!isAuthenticated) {
      alert('You must be logged in as an admin to save articles.');
      return;
    }

    if (isEditing && editingArticle) {
      // Update existing article in Firestore
      const result = await updateArticle(editingArticle.id, articleData);
      if (result.success) {
        // Update local state
        setArticles(articles.map(article => 
          article.id === editingArticle.id 
            ? { ...articleData, id: editingArticle.id, slug: result.slug }
            : article
        ));
        alert('Article updated successfully!');
      } else {
        alert('Error updating article: ' + result.error);
        return;
      }
    } else {
      // Add new article to Firestore
      const result = await addArticle(articleData);
      if (result.success) {
        // Add to local state with the new ID
        const newArticle = {
          ...articleData,
          id: result.id,
          slug: result.slug
        };
        setArticles([newArticle, ...articles]);
        alert('Article added successfully!');
      } else {
        alert('Error adding article: ' + result.error);
        return;
      }
    }
    setShowForm(false);
    setIsEditing(false);
    setEditingArticle(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingArticle(null);
  };

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will add default articles to the database. Continue?')) {
      return;
    }
    
    try {
      const result = await seedArticles();
      alert(`Seeding complete!\nSuccessfully added: ${result.successCount} articles\nFailed: ${result.errorCount} articles`);
      // Reload articles
      const articlesResult = await getAllArticles();
      if (articlesResult.success) {
        setArticles(articlesResult.data);
      }
    } catch (error) {
      alert('Error seeding database: ' + error.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const email = loginData.email.trim();
      console.log('Attempting login with email:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, loginData.password);
      console.log('Login successful:', userCredential.user.email);
      setLoginData({ email: '', password: '' });
      // The auth state change will be handled by onAuthStateChanged
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Invalid credentials or network error');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setLoginData({ email: '', password: '' });
      setLoginError('');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Show loading state while checking auth
  if (checkingAuth) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-page container">
          <div className="admin-header">
            <h1>Admin Portal</h1>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-page container">
          <div className="admin-header">
            <h1>Admin Portal</h1>
            <p>Please log in to manage news articles</p>
          </div>

          <div className="login-container">
            <form onSubmit={handleLogin} className="login-form">
              <h2>Login</h2>
              {loginError && <div className="login-error">{loginError}</div>}
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  required
                  placeholder="Enter your password"
                />
              </div>

              <button type="submit" className="login-btn">
                Login
              </button>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show admin interface if authenticated
  return (
    <div className="admin-page-wrapper">
      <div className="admin-page container">
        <div className="admin-header">
          <h1>Admin Portal</h1>
          <p>Manage news articles for the website</p>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>

        {!showForm ? (
          <div className="admin-content">
            <div className="admin-actions">
              {isAuthenticated ? (
                <>
                  <button className="add-article-btn" onClick={handleAddArticle}>
                    Add New Article
                  </button>
                  {articles.length === 0 && (
                    <button 
                      className="add-article-btn" 
                      onClick={handleSeedDatabase}
                      style={{ backgroundColor: '#4CAF50', marginLeft: '10px' }}
                    >
                      Seed Database with Default Articles
                    </button>
                  )}
                </>
              ) : (
                <p className="login-prompt">Please log in to add or edit articles</p>
              )}
              <Link to="/news" className="view-news-btn">
                View News Page
              </Link>
            </div>

            <div className="articles-list">
              <h2>Articles ({articles.length})</h2>
              <div className="articles-grid">
                {articles.map(article => (
                  <div key={article.id} className="article-card">
                    <div className="article-thumbnail">
                      <img src={article.thumbnail} alt={article.title} />
                    </div>
                    <div className="article-content">
                      <h3>{article.title}</h3>
                      <p className="article-author">By {article.author}</p>
                      <p className="article-date">
                        {new Date(article.datePublished).toLocaleDateString()}
                      </p>
                      <p className="article-preview">{article.firstSentence}</p>
                    </div>
                    {isAuthenticated && (
                      <div className="article-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditArticle(article)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteArticle(article.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ArticleForm
            article={editingArticle}
            onSave={handleSaveArticle}
            onCancel={handleCancel}
            isEditing={isEditing}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

// Article Form Component
const ArticleForm = ({ article, onSave, onCancel, isEditing }) => {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    firstSentence: article?.firstSentence || '',
    thumbnail: article?.thumbnail || '',
    datePublished: article?.datePublished || new Date().toISOString().split('T')[0],
    article: article?.article || '',
    author: article?.author || ''
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      alert('Please select an image first');
      return;
    }

    setUploading(true);
    const result = await uploadImage(imageFile);
    setUploading(false);

    if (result.success) {
      setFormData(prev => ({
        ...prev,
        thumbnail: result.url
      }));
      alert('Image uploaded successfully!');
      setImageFile(null);
    } else {
      alert('Error uploading image: ' + result.error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.firstSentence || !formData.article || !formData.author) {
      alert('Please fill in all required fields');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="article-form-container">
      <h2>{isEditing ? 'Edit Article' : 'Add New Article'}</h2>
      <form onSubmit={handleSubmit} className="article-form">
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="firstSentence">First Sentence *</label>
          <textarea
            id="firstSentence"
            name="firstSentence"
            value={formData.firstSentence}
            onChange={handleChange}
            rows="2"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="thumbnail">Thumbnail URL</label>
          <input
            type="url"
            id="thumbnail"
            name="thumbnail"
            value={formData.thumbnail}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
          />
          <div style={{ marginTop: '10px' }}>
            <label htmlFor="imageUpload" style={{ fontWeight: 'normal', fontSize: '14px' }}>
              Or upload an image:
            </label>
            <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
              <input
                type="file"
                id="imageUpload"
                accept="image/*"
                onChange={handleImageChange}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleImageUpload}
                disabled={!imageFile || uploading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: uploading ? '#ccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: uploading || !imageFile ? 'not-allowed' : 'pointer'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="datePublished">Date Published *</label>
          <input
            type="date"
            id="datePublished"
            name="datePublished"
            value={formData.datePublished}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="author">Author *</label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="article">Full Article *</label>
          <textarea
            id="article"
            name="article"
            value={formData.article}
            onChange={handleChange}
            rows="8"
            required
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" className="save-btn">
            {isEditing ? 'Update Article' : 'Add Article'}
          </button>
      </div>
      </form>
    </div>
  );
};

export default Admin; 